/**
 * Portfolio table toolbar — a debounced loan-id search. Controlled and presentational:
 * the current value comes in as a prop and changes go out through a callback (the page
 * maps it to URL state), so the toolbar stories without a router. The search input keeps
 * its own immediate local value and debounces the outward change so typing stays smooth.
 *
 * Readiness / activity filtering moved into the Segment Builder panel (the segment is the
 * one place a slice is defined); this toolbar is now loan-id search only.
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from '~/components/ui';
import { Search } from '~/components/icons';
import styles from './PortfolioToolbar.module.css';

export interface PortfolioToolbarProps {
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

export function PortfolioToolbar({ search, onSearchChange }: PortfolioToolbarProps): ReactNode {
  const { t } = useTranslation();
  const [value, setValue] = useState(search);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync when the URL-driven search changes from outside (e.g. back nav).
  useEffect(() => setValue(search), [search]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const handleSearch = (next: string): void => {
    setValue(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearchChange(next), SEARCH_DEBOUNCE_MS);
  };

  return (
    <div className={styles.toolbar} data-testid="portfolio-toolbar">
      <div className={styles.search}>
        <TextInput
          value={value}
          onChange={(event) => handleSearch(event.currentTarget.value)}
          leftSection={<Search size={16} aria-hidden />}
          placeholder={t('portfolio.toolbar.searchPlaceholder')}
          aria-label={t('portfolio.toolbar.searchLabel')}
          // F124: the search matches the HMAC'd protectedLoanId, never a raw loan number — say so.
          description={t('portfolio.toolbar.searchHelper')}
          data-testid="portfolio-search"
        />
      </div>
    </div>
  );
}
